import React from "react";
import { Button } from "@/components/ui/button";
import { Rocket, HelpCircle } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useShowGettingStarted } from "./FloatingGettingStartedProvider";

interface GettingStartedTriggerProps {
  variant?: "default" | "outline" | "ghost" | "link" | "destructive" | "secondary";
  size?: "default" | "sm" | "lg" | "icon";
  showText?: boolean;
  tooltipText?: string;
  className?: string;
}

/**
 * A trigger button that can be placed anywhere in the app
 * to manually show the getting started guide
 */
export const GettingStartedTrigger = ({
  variant = "ghost",
  size = "sm",
  showText = false,
  tooltipText = "Show getting started guide",
  className = "",
}: GettingStartedTriggerProps) => {
  const showGuide = useShowGettingStarted();

  const ButtonContent = () => (
    <>
      <Rocket className="h-4 w-4" />
      {showText && <span className="ml-2">Getting Started</span>}
    </>
  );

  const button = (
    <Button
      variant={variant}
      size={size}
      onClick={showGuide}
      className={`${showText ? "" : size === "icon" ? "h-8 w-8" : ""} ${className}`}
    >
      <ButtonContent />
    </Button>
  );

  if (!showText && tooltipText) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            {button}
          </TooltipTrigger>
          <TooltipContent>
            <p>{tooltipText}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return button;
};

/**
 * Alternative help-style trigger button
 */
export const HelpGettingStartedTrigger = ({
  variant = "ghost",
  size = "sm",
  showText = false,
  className = "",
}: {
  variant?: "default" | "outline" | "ghost" | "link" | "destructive" | "secondary";
  size?: "default" | "sm" | "lg" | "icon";
  showText?: boolean;
  className?: string;
}) => {
  const showGuide = useShowGettingStarted();

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant={variant}
            size={size}
            onClick={showGuide}
            className={`${showText ? "" : size === "icon" ? "h-8 w-8" : ""} ${className}`}
          >
            <HelpCircle className="h-4 w-4" />
            {showText && <span className="ml-2">Need Help?</span>}
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>Show setup guide</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}; 